import axios from "axios";
import classnames from "classnames";
import jwt_decode from "jwt-decode";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import VismaEEconomyImg from "@krea/customer-features/images/logo-visma-eekonomi.jpeg";
import FortnoxImg from "@krea/customer-features/images/svg/accounting/fortnox.svg";
import { settings } from "@krea/customer-features/settings";
import Button from "@krea/customer-features/shared-components/button";
import Fieldset from "@krea/customer-features/shared-components/fields/fieldset";
import TextInput from "@krea/customer-features/shared-components/fields/input";
import { Label } from "@krea/customer-features/shared-components/fields/label";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { Text } from "@krea/customer-features/shared-components/text";
import { isValidEmail, ssnValidator } from "@krea/customer-features/utils";

import styles from "./AccountingIntegrationApp.module.scss";
import { ErpSystemsList } from "./ErpSystemsList";

const axiosClient = axios.create({
  baseURL: null,
  withCredentials: false,
  xsrfCookieName: null,
  xsrfHeaderName: null,
});

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error.response);
  },
);

const FlowStatus = {
  ONGOING_FLOW: "ONGOING_FLOW",
  BANK_ID_WITHOUT_INPUT_NEEDED_SUCCESS: "BANK_ID_WITHOUT_INPUT_NEEDED_SUCCESS",
  INPUT_CHOOSE_COMPANY: "INPUT_CHOOSE_COMPANY",
  INPUT_VERIFICATION_CODE: "INPUT_VERIFICATION_CODE",
  NO_EXPORT_ACCESS: "NO_EXPORT_ACCESS",
  NO_DATA_FOUND: "NO_DATA_FOUND",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR", // the flow errored out in an unexpected/non-handled way
  ABORTED: "ABORTED", // non error abortion, eg. user aborted bankId
};

const LoginMethod = {
  BANK_ID: "BANK_ID",
  USERNAME_PASSWORD: "USERNAME_PASSWORD",
};

const VENDOR_SETTINGS = {
  FORTNOX: {
    // initialLoginMethod: LoginMethod.BANK_ID,
    // supportedLoginMethods: [LoginMethod.BANK_ID, LoginMethod.USERNAME_PASSWORD]
    initialLoginMethod: LoginMethod.USERNAME_PASSWORD,
    supportedLoginMethods: [LoginMethod.USERNAME_PASSWORD],
  },
  VISMA_E_ECONOMY: {
    initialLoginMethod: LoginMethod.USERNAME_PASSWORD,
    supportedLoginMethods: [LoginMethod.USERNAME_PASSWORD],
  },
};

const Loading = () => {
  return <Preloader className="text-primary" />;
};

const AccountingMessages = ({ className, children }) => {
  return (
    <Text
      className={classnames("text-center font-weight-bold mb-5", className)}
    >
      {children}
    </Text>
  );
};

const AccountingIntegrationApp = ({
  applyingCompanyID,
  erpSystem,
  onSuccess,
  onFailure,
  hasParentFieldRequest,
}) => {
  const [erpSystemBackendName, setErpSystemBackendName] = useState(
    ErpSystemsList[erpSystem].backendName,
  );
  const [loginMethod, setLoginMethod] = useState(
    VENDOR_SETTINGS[ErpSystemsList[erpSystem].backendName].initialLoginMethod,
  );
  const [ssn, setSsn] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [companyChoices, setCompanyChoices] = useState([]);

  const [auth, setAuth] = useState({
    clientToken: null,
    decodedClientToken: {},
    accountingDataId: null,
  });

  const [hasPassedAuthentication, setHasPassedAuthentication] = useState(false);
  const [flowStatus, setFlowStatus] = useState(FlowStatus.ONGOING_FLOW);

  const [inputError, setInputError] = useState({});

  const [isMutatingFlowRequestIsLoading, setMutatingFlowRequestIsLoading] =
    useState(false);
  const [
    hasClickedVerificationCodeButton,
    setHasClickedVerificationCodeButton,
  ] = useState(false);

  let getFlowInterval = null;

  const resetState = () => {
    setSsn("");
    setUsername("");
    setPassword("");
    setVerificationCode("");
    setCompanyChoices([]);
    setAuth({
      clientToken: null,
      decodedClientToken: {},
      accountingDataId: null,
    });
    setHasPassedAuthentication(false);
    setFlowStatus(FlowStatus.ONGOING_FLOW);
    setLoginMethod(
      VENDOR_SETTINGS[ErpSystemsList[erpSystem].backendName].initialLoginMethod,
    );
    setErpSystemBackendName(ErpSystemsList[erpSystem].backendName);
    setInputError({});
    setMutatingFlowRequestIsLoading(false);
    setHasClickedVerificationCodeButton(false);
  };

  const { t } = useTranslation();

  useEffect(() => {
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erpSystem]);

  useEffect(() => {
    return () => {
      if (getFlowInterval) {
        clearInterval(getFlowInterval);
      }
    };
  });

  const onChange = ({ target }) => {
    if (target.id === "input_accounting_ssn") {
      setInputError({ input_accounting_ssn: null });
      setSsn(target.value.replace("-", "").trim());
    } else if (target.id === "input_accounting_username") {
      setInputError({ input_accounting_username: null });
      setUsername(target.value);
    } else if (target.id === "input_accounting_password") {
      setInputError({ input_accounting_password: null });
      setPassword(target.value);
    } else if (target.id === "input_accounting_verification_code") {
      setInputError({ input_accounting_verification_code: null });
      setVerificationCode(target.value);
    }
  };

  const getHeaders = (authData) => {
    return {
      Authorization: `Bearer ${authData.clientToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
    };
  };

  const onStartFlow = async () => {
    if (loginMethod === LoginMethod.BANK_ID) {
      if (!ssnValidator(ssn, settings.countryCode).isValid) {
        setInputError({
          input_accounting_ssn: t(
            "iaq.accountingData.integration.invalidSsnMessage",
          ),
        });

        return;
      } else {
        if (ssn.length !== 12) {
          setInputError({
            input_accounting_ssn: t(
              "iaq.accountingData.integration.invalidSsnLengthMessage",
            ),
          });

          return;
        }
      }
    }

    if (loginMethod === LoginMethod.USERNAME_PASSWORD) {
      if (erpSystemBackendName === "VISMA_E_ECONOMY") {
        if (!isValidEmail(username)) {
          setInputError({
            input_accounting_username: t(
              "iaq.accountingData.integration.vismaEEconomy.invalidUsernameMessage",
            ),
          });

          return;
        }
      }

      if (!username || !password) {
        if (erpSystemBackendName === "FORTNOX") {
          if (!username) {
            setInputError({
              input_accounting_username: t(
                "iaq.accountingData.integration.fortnox.requiredUsernameMessage",
              ),
            });
          }
        }

        if (erpSystemBackendName === "VISMA_E_ECONOMY") {
          if (!username) {
            setInputError({
              input_accounting_username: t(
                "iaq.accountingData.integration.vismaEEconomy.requiredUsernameMessage",
              ),
            });
          }
        }

        if (!password) {
          setInputError({
            input_accounting_password: t(
              "iaq.accountingData.integration.requiredPasswordMessage",
            ),
          });
        }

        return;
      }
    }

    const erpSystemInfo = ErpSystemsList[erpSystem];
    const payload = {
      applying_company_id: applyingCompanyID,
      service_type: "ACCOUNTING_INTEGRATION",
      erp_system: erpSystemInfo.backendName,
      login_method: loginMethod,
      ssn,
      username,
      password,
    };
    setMutatingFlowRequestIsLoading(true);

    try {
      const response = await axios.post(
        "/accounting-service/api/v1/accounting-data",
        payload,
      );

      const clientToken = response.data.callback_token;
      const decodedClientToken = jwt_decode(clientToken);
      const authData = {
        clientToken,
        decodedClientToken,
        accountingDataId: response.data.accounting_data_id,
      };
      setAuth(authData);
      startPollFlow(authData);
    } catch (errorResponse) {
      setFlowStatus(FlowStatus.ERROR);
    }
    setMutatingFlowRequestIsLoading(false);
  };

  const startPollFlow = (authData) => {
    if (getFlowInterval) {
      clearInterval(getFlowInterval);
    }
    getFlowInterval = setInterval(async () => {
      try {
        const response = await axiosClient.get(
          authData.decodedClientToken.flowUrl,
          {
            headers: getHeaders(authData),
          },
        );
        const data = response.data;
        const { flowStatus, companyChoices, result, hasPassedAuthentication } =
          data;

        setFlowStatus(flowStatus);
        setHasPassedAuthentication(hasPassedAuthentication);

        if (flowStatus === FlowStatus.INPUT_CHOOSE_COMPANY) {
          setCompanyChoices(companyChoices);
        }

        // if flowStatus is anything but ONGOING_FLOW or "pre authentication complete" statuses, we want to "stop" and give feedback/take actions.
        if (
          ![
            FlowStatus.ONGOING_FLOW,
            FlowStatus.INPUT_CHOOSE_COMPANY,
            FlowStatus.BANK_ID_WITHOUT_INPUT_NEEDED_SUCCESS,
            FlowStatus.INPUT_VERIFICATION_CODE,
          ].includes(flowStatus)
        ) {
          if (getFlowInterval) {
            clearInterval(getFlowInterval);
          }

          if (flowStatus === FlowStatus.SUCCESS) {
            onSuccess({
              ...result,
              accountingDataId: authData.accountingDataId,
            });
            await onAbortFlow(authData, false);
          } else {
            await onAbortFlow(authData, false);
          }
        } else {
          console.log("keep polling...");
        }
      } catch (errorResponse) {
        // If we get a 404 here, its very likely its a raise condition that the GET request is triggered
        // a the same time as we aborted with a DELETE to the flow. Then milliseconds later the GET gives us a 404,
        // since the flow is deleted by then. That's why we dont set flowStatus to ERROR on a 404 here.
        if (errorResponse.status !== 404) {
          setFlowStatus(FlowStatus.ERROR);
        }

        if (getFlowInterval) {
          clearInterval(getFlowInterval);
        }
      }
    }, 2000);
  };

  const updateFlow = async (
    verificationCode = null,
    companyNameInput = null,
  ) => {
    const payload = {
      ...auth.decodedClientToken.flowData,
      flowStatus,
      ssn,
      username,
      password,
      verificationCode,
      companyNameInput,
    };
    setMutatingFlowRequestIsLoading(true);

    try {
      await axiosClient.put(auth.decodedClientToken.flowUrl, payload, {
        headers: getHeaders(auth),
      });
    } catch (errorResponse) {
      setFlowStatus(FlowStatus.ERROR);
    }
    setMutatingFlowRequestIsLoading(false);
  };

  const onAbortFlow = async (auth, shouldAlterState = true) => {
    if (getFlowInterval) {
      clearInterval(getFlowInterval);
    }

    try {
      await axiosClient.delete(auth.decodedClientToken.flowUrl, {
        headers: getHeaders(auth),
      });

      if (shouldAlterState) {
        setFlowStatus(FlowStatus.ABORTED);
      }
    } catch (errorResponse) {
      setFlowStatus(FlowStatus.ERROR);
    }

    if (shouldAlterState) {
      resetState();
    }
  };

  useEffect(() => {
    if (flowStatus === FlowStatus.BANK_ID_WITHOUT_INPUT_NEEDED_SUCCESS) {
      // on BANK_ID_WITHOUT_INPUT_NEEDED_SUCCESS we want to give user feedback about successfull bankId,
      // then after 3 seconds of
      setTimeout(async () => {
        await updateFlow();
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowStatus]);

  const renderErpSystemTopSection = (erpSystem) => {
    switch (ErpSystemsList[erpSystem].backendName) {
      case ErpSystemsList.fortnox.backendName:
        return (
          <img className="d-block w-100" src={FortnoxImg} alt="fortnox logo" />
        );
      case ErpSystemsList.visma.backendName:
        return (
          <img
            className="d-block w-100"
            src={VismaEEconomyImg}
            alt="visma eEkonomi logo"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="d-block ">
      <div className="d-flex align-items-center justify-content-center">
        <div
          className={classnames(
            styles.logo,
            "d-flex align-items-center justify-content-center",
          )}
        >
          {renderErpSystemTopSection(erpSystem)}
        </div>
      </div>

      <Text
        size="sm"
        className="font-weight-bolder text-center text-gray-dark mb-6"
      >
        {t("iaq.accountingData.integration.informationText1")} <br />
        {t("iaq.accountingData.integration.informationText2")}
      </Text>

      {auth.clientToken ? (
        <div className="d-block">
          <div
            className="d-flex justify-content-center align-items-center my-4"
            style={{ height: 32 }}
          >
            {flowStatus === FlowStatus.ONGOING_FLOW ? <Loading /> : null}
          </div>
          {!hasPassedAuthentication ? ( // Before we have passed authentication steps
            <Fragment>
              {loginMethod === LoginMethod.BANK_ID ? (
                <Fragment>
                  {flowStatus === FlowStatus.ONGOING_FLOW ? (
                    <AccountingMessages>
                      {t("iaq.accountingData.integration.waitingForBankID")}
                    </AccountingMessages>
                  ) : flowStatus ===
                    FlowStatus.BANK_ID_WITHOUT_INPUT_NEEDED_SUCCESS ? (
                    <AccountingMessages>
                      {t("iaq.accountingData.integration.bankIDCompleted")}
                    </AccountingMessages>
                  ) : flowStatus === FlowStatus.INPUT_CHOOSE_COMPANY ? (
                    <div>
                      <div className="my-3 font-weight-bold">
                        {t("iaq.accountingData.integration.chooseCompany")}
                      </div>
                      <ul className="list-unstyled py-1 border-top border-bottom">
                        {companyChoices.map((it, index) => (
                          <li key={index} className="my-2">
                            <Button
                              variant="text"
                              disabled={isMutatingFlowRequestIsLoading}
                              onClick={() => updateFlow(null, it.name)}
                            >
                              {it.name}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : flowStatus === FlowStatus.ABORTED ? (
                    <AccountingMessages>
                      {t("iaq.accountingData.integration.flowAborted")}
                    </AccountingMessages>
                  ) : flowStatus === FlowStatus.ERROR ? (
                    <AccountingMessages>
                      <span>
                        {t("iaq.accountingData.integration.flowError")}
                      </span>
                      {erpSystemBackendName === "FORTNOX" && (
                        <div>
                          {t(
                            "iaq.accountingData.integration.fortnox.flowErrorAdditionalMessage1",
                          )}{" "}
                          <a
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              resetState();
                              setLoginMethod(LoginMethod.USERNAME_PASSWORD);
                            }}
                          >
                            {t(
                              "iaq.accountingData.integration.fortnox.flowErrorAdditionalMessage2",
                            )}
                          </a>{" "}
                          {t(
                            "iaq.accountingData.integration.fortnox.flowErrorAdditionalMessage3",
                          )}
                        </div>
                      )}
                    </AccountingMessages>
                  ) : null}
                </Fragment>
              ) : loginMethod === LoginMethod.USERNAME_PASSWORD ? (
                <Fragment>
                  {flowStatus === FlowStatus.ONGOING_FLOW ? (
                    <AccountingMessages>
                      {" "}
                      {t("iaq.accountingData.integration.flowOngoing")}
                    </AccountingMessages>
                  ) : flowStatus === FlowStatus.INPUT_VERIFICATION_CODE ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        marginBottom: 30,
                      }}
                    >
                      <TextInput
                        autoFocus={!hasParentFieldRequest}
                        type="text"
                        inputMode="text"
                        id={`input_accounting_verification_code`}
                        value={verificationCode}
                        onChange={onChange}
                        placeholder=""
                        label={t(
                          "iaq.accountingData.integration.verificationCodeLabel",
                        )}
                        error={inputError.input_accounting_verification_code}
                      />
                      <div style={{ paddingLeft: 10 }}>
                        <Button
                          style={{ height: 42 }}
                          size="sm"
                          disabled={hasClickedVerificationCodeButton}
                          onClick={() => {
                            setHasClickedVerificationCodeButton(true);
                            updateFlow(verificationCode);
                          }}
                        >
                          {t(
                            "iaq.accountingData.integration.verificationCodeButton",
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : flowStatus === FlowStatus.ABORTED ? (
                    <AccountingMessages>
                      {t("iaq.accountingData.integration.flowAborted")}
                    </AccountingMessages>
                  ) : flowStatus === FlowStatus.ERROR ? (
                    <AccountingMessages className="text-danger">
                      {t("iaq.accountingData.integration.flowError")}
                    </AccountingMessages>
                  ) : null}
                </Fragment>
              ) : null}
            </Fragment>
          ) : (
            // After we have passed authentication steps
            <Fragment>
              {flowStatus === FlowStatus.ONGOING_FLOW ? (
                <AccountingMessages>
                  {t("iaq.accountingData.integration.afterLoginFlowOngoing")}
                </AccountingMessages>
              ) : flowStatus === FlowStatus.SUCCESS ? (
                <AccountingMessages>
                  {t("iaq.accountingData.integration.flowSuccess")}
                </AccountingMessages>
              ) : flowStatus === FlowStatus.ABORTED ? (
                <AccountingMessages>
                  {t("iaq.accountingData.integration.flowAborted")}
                </AccountingMessages>
              ) : flowStatus === FlowStatus.ERROR ? (
                <AccountingMessages className="text-danger">
                  {t("iaq.accountingData.integration.flowError")}
                </AccountingMessages>
              ) : flowStatus === FlowStatus.NO_EXPORT_ACCESS ? (
                <AccountingMessages>
                  {t("iaq.accountingData.integration.flowNoExportAccess")}
                </AccountingMessages>
              ) : flowStatus === FlowStatus.NO_DATA_FOUND ? (
                <AccountingMessages>
                  {t("iaq.accountingData.integration.flowNoDataFound")}
                </AccountingMessages>
              ) : null}
            </Fragment>
          )}
          {!hasPassedAuthentication ? (
            <div className="d-flex justify-content-center">
              <Button
                size="sm"
                className="px-5"
                onClick={() => onAbortFlow(auth)}
              >
                {t("iaq.accountingData.integration.cancelButton")}
              </Button>
            </div>
          ) : (
            <Fragment>
              {[
                FlowStatus.NO_DATA_FOUND,
                FlowStatus.NO_EXPORT_ACCESS,
                FlowStatus.ABORTED,
                FlowStatus.ERROR,
              ].includes(flowStatus) ? (
                <div className="d-flex justify-content-center">
                  <Button
                    variant="outlined"
                    size="sm"
                    className="px-6"
                    onClick={resetState}
                  >
                    {t("iaq.accountingData.integration.okButton")}
                  </Button>
                </div>
              ) : null}
            </Fragment>
          )}
        </div>
      ) : (
        <Fragment>
          <div className="my-3 font-weight-bold">
            {loginMethod === LoginMethod.BANK_ID
              ? t("iaq.accountingData.integration.bankIdOptionTitle")
              : t("iaq.accountingData.integration.usernamePasswordOptionTitle")}
          </div>
          <div style={{ display: "block" }}>
            {loginMethod === LoginMethod.BANK_ID ? (
              <TextInput
                autoFocus={!hasParentFieldRequest}
                type="text"
                pattern="[0-9]*"
                inputMode="number"
                id={`input_accounting_ssn`}
                value={ssn}
                onChange={onChange}
                placeholder="YYYYMMDDXXXX"
                label={t("iaq.accountingData.integration.ssnLabel")}
                error={inputError.input_accounting_ssn}
                autoComplete="off"
              />
            ) : (
              <Fieldset>
                <TextInput
                  autoFocus={!hasParentFieldRequest}
                  type="text"
                  inputMode="text"
                  id={`input_accounting_username`}
                  value={username}
                  onChange={onChange}
                  placeholder={
                    erpSystemBackendName === "FORTNOX"
                      ? t(
                          "iaq.accountingData.integration.fortnox.usernamePlaceholder",
                        )
                      : t(
                          "iaq.accountingData.integration.vismaEEconomy.usernamePlaceholder",
                        )
                  }
                  label={
                    erpSystemBackendName === "FORTNOX"
                      ? t(
                          "iaq.accountingData.integration.fortnox.usernameLabel",
                        )
                      : t(
                          "iaq.accountingData.integration.vismaEEconomy.usernameLabel",
                        )
                  }
                  error={inputError.input_accounting_username}
                  autoComplete="off"
                />

                <TextInput
                  autoFocus={false}
                  type="password"
                  inputMode="text"
                  id={`input_accounting_password`}
                  value={password}
                  onChange={onChange}
                  placeholder=""
                  label={t("iaq.accountingData.integration.passwordLabel")}
                  error={inputError.input_accounting_password}
                  autoComplete="off"
                />
              </Fieldset>
            )}
          </div>

          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-4 mt-md-6">
            <div className="order-1 order-md-0 mt-3 m-md-0">
              {ErpSystemsList[erpSystem] &&
                VENDOR_SETTINGS[
                  ErpSystemsList[erpSystem].backendName
                ].supportedLoginMethods
                  .filter(
                    (supportedLoginMethod) =>
                      supportedLoginMethod !== loginMethod,
                  )
                  .map((supportedLoginMethod) => (
                    <div
                      key={supportedLoginMethod}
                      style={{ display: "block" }}
                      className="pb-2"
                    >
                      <div className="d-inline-block align-middle mr-1">
                        <Label>
                          {t(
                            "iaq.accountingData.integration.loginMethodChangeTo",
                          )}
                        </Label>
                      </div>
                      <div className="d-inline-block align-middle">
                        <Button
                          variant="text"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            setLoginMethod(
                              loginMethod === LoginMethod.BANK_ID
                                ? LoginMethod.USERNAME_PASSWORD
                                : LoginMethod.BANK_ID,
                            );
                          }}
                        >
                          {loginMethod === LoginMethod.BANK_ID
                            ? t(
                                "iaq.accountingData.integration.usernamePasswordOptionTitle",
                              )
                            : t(
                                "iaq.accountingData.integration.bankIdOptionTitle",
                              )}
                        </Button>
                      </div>
                    </div>
                  ))}
            </div>

            <div className="d-flex align-items-center justify-content-end order-0 order-md-1">
              <Button
                variant={
                  ssn || (username && password) ? "contained" : "outlined"
                }
                size="sm"
                className="px-5"
                disabled={isMutatingFlowRequestIsLoading}
                onClick={onStartFlow}
              >
                {t("iaq.accountingData.integration.CTAButton")}
              </Button>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default AccountingIntegrationApp;
